// The words the report dashboard uses, per subject.
//
// Supplied the same way the fetchers are, and for the same reason: the strings that have to
// change for English sit inside components a thousand lines long, several of them nested, and
// threading a subject prop through every layer would touch code that has no interest in it. The
// page chooses a set once; a component asks for "the copy".
//
// The default is the MATHS set, so a component nobody has converted reads exactly as it did
// before this existed - which means this can be adopted one string at a time.
//
// Only words that are WRONG for the other subject belong here. "Mastery Breakdown", "Skills Gap",
// "Not attempted" and the rest are true of both, and duplicating them per subject would be two
// places to edit for one change.

import React from "react"

/** One wrong-answer bucket on the Skills Gap tab: a card, a badge and the dot on its rows. */
export type GapOutcome = {
  /** The `misconception_type` this bucket counts, as marking writes it. */
  key: string
  label: string
  /**
   * One line saying what the bucket MEANS, for the buckets whose name does not say it.
   *
   * Optional on purpose: the three misconception types are the curriculum team's own words and
   * need no gloss from us. "Untagged" does - the PM asked outright what it was.
   */
  hint?: string
  bg: string
  text: string
  dot: string
}

/**
 * Bucket key for a wrong answer carrying no misconception tag.
 *
 * Uppercase so it can never collide with a real code, which arrives uppercased. A subject opts
 * in by putting an outcome with this key in `gapOutcomes`; maths has not, and keeps the three
 * cards it shipped with.
 */
export const UNTAGGED_KEY = "UNTAGGED"

export type ReportCopy = {
  /** Empty state when the student has never sat a diagnostic in this subject. */
  noDataForStudent: string
  /**
   * The pill on an answered topic block.
   *
   * Maths splits it by what the block still owes - an FM retry, a prerequisite test, or both -
   * from counts the maths backend sends. English owes neither: there is no fundamental-
   * misconception retry, and no Skill Builders to remediate with, so a block that has been
   * answered is simply done.
   */
  completedPill: string
  /** Whether the FM / prerequisite split applies to that pill at all. */
  splitCompletedPill: boolean
  /** Wrong-answer buckets, in display order. */
  gapOutcomes: readonly GapOutcome[]
  /**
   * What the KPI strip's `topics` count IS.
   *
   * Both subjects count session TOPIC BLOCKS, but the word differs: maths blocks are the unit
   * the business calls a cluster, while an English block is one topic and several of them sit
   * inside one cluster. Calling the English number "9 clusters" put it next to a radar drawn
   * with 6 cluster axes, 2 of them with data.
   */
  kpiGroupNoun: { one: string; many: string }
  /**
   * What one row of the Skills Gap table IS.
   *
   * Maths marks one answer per question, so a row is a skill and the table counts "N of M
   * attempted skills". English marks per PART and folds a question's parts back together, so a
   * row is one question's work on one skill. Getting this wrong is how the caption came to read
   * "42 of 21".
   */
  gapRowNoun: { one: string; many: string }
  /** Heading above that table, for the same reason. */
  gapTableTitle: string
  /**
   * What ONE ROW of the Diagnostic Attempts table stands for.
   *
   * "block" is a single session topic block, which is what both subjects store. "strand" rolls
   * those blocks up: the English UI names the CLUSTER everywhere a child or tutor sees one -
   * the question player's pill does the same - and a Year 1 English test puts eight topic
   * blocks inside Grammar, which listed flat is eight rows a tutor reads as repetition.
   *
   * The heading stays "Topic" either way. A rolled-up row expands to its blocks, because
   * Preview and Chain act on a block and must not be lost.
   */
  attemptGrouping: "block" | "strand"
}

/**
 * Maths. Three misconception types, hardcoded in the maths schema as a bare varchar.
 *
 * The colours are the ones the tab shipped with and are load-bearing for recognition: red is the
 * foundational one that earns a retry, blue the recently-taught one, amber the shallow one.
 */
export const MATH_COPY: ReportCopy = {
  noDataForStudent: "No BMD data yet for this student.",
  completedPill: "Pending remediation",
  splitCompletedPill: true,
  kpiGroupNoun: { one: "cluster", many: "clusters" },
  gapRowNoun: { one: "attempted skill", many: "attempted skills" },
  gapTableTitle: "Skills with Gaps (Skill Breakdown)",
  attemptGrouping: "block",
  gapOutcomes: [
    { key: "correct", label: "Correct", bg: "#d1fae5", text: "#047857", dot: "#10b981" },
    {
      key: "FUNDAMENTAL",
      label: "Fundamental misconception",
      bg: "#fde2e1",
      text: "#b42318",
      dot: "#dc3545",
    },
    {
      key: "RECENTLY_LEARNED",
      label: "Confusion with recently learned skill",
      bg: "#dbeafe",
      text: "#1d4ed8",
      dot: "#3b82f6",
    },
    { key: "LACK_OF_DEPTH", label: "Lack of Depth", bg: "#fef3c7", text: "#92400e", dot: "#f59e0b" },
  ],
}

/**
 * English. Also three types, but its own: `eng_fdiag_misconception_type` holds them as data with
 * semantic codes, seeded in _local/eng-fdiag.sql. They line up with the maths three closely
 * enough to keep the colours, so a tutor who works in both subjects reads the same red for "an
 * earlier skill is missing" in each.
 *
 * The labels here are the seeded ones. A row also carries `misconception_label` straight from
 * that table, which wins where present - so renaming a type in the database renames it on the
 * report without a deploy.
 */
export const ENGLISH_COPY: ReportCopy = {
  noDataForStudent: "No English diagnostics yet for this student.",
  completedPill: "Completed",
  splitCompletedPill: false,
  // An English block is one TOPIC; the clusters are the radar's axes, a rung above.
  kpiGroupNoun: { one: "topic", many: "topics" },
  // A row is one QUESTION's work on one skill: the parts of a question are folded together,
  // because an image_sort marked per item produced eight rows a tutor could not tell apart.
  // Several questions can still test the same skill, and those stay separate rows.
  gapRowNoun: { one: "question", many: "questions" },
  gapTableTitle: "Questions by Skill",
  attemptGrouping: "strand",
  gapOutcomes: [
    { key: "correct", label: "Correct", bg: "#d1fae5", text: "#047857", dot: "#10b981" },
    {
      key: "FOUNDATIONAL_GAPS",
      label: "Type B - Foundational Gaps",
      bg: "#fde2e1",
      text: "#b42318",
      dot: "#dc3545",
    },
    {
      key: "FRAGILE_NEW_LEARNING",
      label: "Type A - Fragile New Learning",
      bg: "#dbeafe",
      text: "#1d4ed8",
      dot: "#3b82f6",
    },
    {
      key: "SHALLOW_DEPTH",
      label: "Type C - Shallow Depth",
      bg: "#fef3c7",
      text: "#92400e",
      dot: "#f59e0b",
    },
    // Last, and grey: not a diagnosis, just an answer the author catalogued no mistake for.
    // Without it the cards said 35 correct + 1 + 0 + 0 against 42 answers.
    {
      key: UNTAGGED_KEY,
      label: "Untagged",
      // Says what it is rather than what it is not. These answers ARE wrong and ARE counted;
      // all that is missing is a named misconception, because what the child wrote was not one
      // of the wrong answers the question's author wrote down. A tutor should read them as
      // "worth a look" rather than as a category of mistake.
      hint: "Wrong, but not one of the mistakes the author listed - so no type was given",
      bg: "#f3f4f6",
      text: "#374151",
      dot: "#9ca3af",
    },
  ],
}

const ReportCopyContext = React.createContext<ReportCopy>(MATH_COPY)

export const ReportCopyProvider: React.FC<{
  value: ReportCopy
  children: React.ReactNode
}> = ({ value, children }) => (
  <ReportCopyContext.Provider value={value}>{children}</ReportCopyContext.Provider>
)

/** The copy for whichever subject is on screen. Maths unless a provider says otherwise. */
export const useReportCopy = (): ReportCopy => React.useContext(ReportCopyContext)
